import { IntegrationFlowOAuth2AuthenticationSettings } from "client/Api/IntegrationBuilder/integrationBuilderAuthentication";
import {
	CopyControl,
	FormItem,
	SecretValueInput,
	TextInput,
	ValidationError
} from "client/Components";
import { IValueInputAttachable } from "client/Forms/attachable";
import { localize } from "client_legacy/ndaw/culture";
import { ReactElement, useCallback } from "react";
import { ScopeCollection } from "../../ScopeCollection";
import {
	doesNotExceedLength,
	hasMaximumEntries,
	isNotEmpty,
	isNotNullNorUndefined,
	isUrl
} from "client/Validation/rules";
import { createValidationConfiguration, IValidationConfiguration } from "client/Validation";

export type OAuth2SettingsProps = {
	attachable: IValueInputAttachable<IntegrationFlowOAuth2AuthenticationSettings>;
	isNew: boolean;
	redirectUrl: string | null;
};

export const OAuth2Settings = (props: OAuth2SettingsProps): ReactElement => {
	const {
		attachable,
		isNew,
		redirectUrl
	} = props;

	const resetClientSecretHandler = useCallback(
		(): void => {
			const {
				clientSecret
			} = attachable.value;

			if (clientSecret.containsNewValue) {
				return;
			}

			attachable.for("clientSecret")
				.reducer
				.setValue({
					containsNewValue: true,
					value: ""
				});

			attachable.for("clientSecret")
				.validation
				.triggerInputFinished();
		},
		[attachable]
	);

	return (
		<>
			<FormItem label={localize("_AuthorizationEndpointURL")} isRequired>
				<TextInput
					{...attachable.for("authorizationUrl").attachToTextInput()
						.withOnChange(resetClientSecretHandler)
					}
					placeholder={localize("_EnterAuthorizationEndpointURL")}
				/>

				<ValidationError {...attachable.for("authorizationUrl").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_TokenEndpointURL")} isRequired>
				<TextInput
					{...attachable.for("tokenUrl").attachToTextInput()
						.withOnChange(resetClientSecretHandler)
					}
					placeholder={localize("_EnterTokenEndpointURL")}
				/>

				<ValidationError {...attachable.for("tokenUrl").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_ClientId")} isRequired>
				<TextInput
					{...attachable.for("clientId").attachToTextInput()
						.withOnChange(resetClientSecretHandler)
					}
					placeholder={localize("_EnterClientID")}
				/>

				<ValidationError {...attachable.for("clientId").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_ClientSecret")} isRequired>
				<SecretValueInput
					{...attachable.for("clientSecret").attachToSecretValueInput()}
					containsInitialValue={!isNew}
					placeholder={localize("_EnterClientSecret")}
				/>

				<ValidationError {...attachable.for("clientSecret").for("value").attachToValidationError()} />
			</FormItem>

			<FormItem label={localize("_Scopes")}>
				<ScopeCollection
					attachable={attachable.for("scopes")}
					maxCount={20}
				/>
			</FormItem>

			{redirectUrl && (
				<FormItem label={localize("_RedirectUrl")}>
					<CopyControl
						value={redirectUrl}
						trackingId="oauth2-redirect-url"
						buttonKind="secondary"
					/>
				</FormItem>
			)}
		</>
	);
};
OAuth2Settings.displayName = "OAuth2Settings";

export const defaultOAuth2Settings: IntegrationFlowOAuth2AuthenticationSettings = {
	tokenUrl: "",
	authorizationUrl: "",
	clientId: "",
	clientSecret: {
		containsNewValue: true,
		value: ""
	},
	scopes: []
};

export const oAuth2ValidationConfiguration: IValidationConfiguration<IntegrationFlowOAuth2AuthenticationSettings> = createValidationConfiguration<IntegrationFlowOAuth2AuthenticationSettings>();

oAuth2ValidationConfiguration.forScalar("authorizationUrl")
	.add(
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true
		}),
		doesNotExceedLength(2_048)
	);

oAuth2ValidationConfiguration.forScalar("tokenUrl")
	.add(
		isNotEmpty(),
		isUrl({
			allowHttpsOnly: true
		}),
		doesNotExceedLength(2_048)
	);

oAuth2ValidationConfiguration.forScalar("clientId")
	.add(
		isNotEmpty(),
		doesNotExceedLength(255)
	);

oAuth2ValidationConfiguration.forObject("clientSecret")
	.when(x => x.containsNewValue)
	.forScalar("value")
	.add(
		isNotNullNorUndefined(),
		isNotEmpty(),
		doesNotExceedLength(255)
	);

oAuth2ValidationConfiguration.forCollection("scopes")
	.add(
		hasMaximumEntries(20)
	)
	.forItems()
	.add(
		isNotEmpty()
	);